// Tap-to-advance for the hero slider, mobile only.
//
// Loaded ONLY when the merchant has hidden BOTH the pagination dots and the arrows on mobile (see the
// guard in sections/hero-slider.liquid). In that state a swipe — a dragging gesture — would be the
// only way to change slides, which fails WCAG 2.5.7 (Dragging Movements) / 2.5.1 (Pointer Gestures).
// This restores a single-pointer alternative with zero visible chrome: a plain tap on the active
// slide advances to the next one.
//
// Care taken:
//   - Mobile only (≤749px); desktop keeps keyboard + its own controls.
//   - A drag/swipe is NOT a tap — we ignore the click if the pointer moved past a small threshold,
//     so swiping still navigates normally and never double-fires.
//   - Interactive content is never hijacked: taps on links, buttons, the drawer trigger/sheet, form
//     controls, etc. do their own thing.
//   - Only the ACTIVE slide advances. In the "hints/peek" display mode the non-active peeking slides
//     keep their built-in `on:click="/select/{index}"` navigate-to-slide behavior (they're
//     aria-hidden="true"; the active slide is aria-hidden="false").
//   - The active slide also carries `on:click="/select/{index}"`, delegated on `document` in the
//     capture phase (assets/component.js). On the active slide that selects the slide that's already
//     current, which the engine treats as a clean no-op — it early-returns before dispatching any
//     event or animating (assets/slideshow.js:229). So it costs nothing and we don't try to suppress
//     it; we simply advance on top of it.
//   - We CYCLE (wrap to the first slide after the last) rather than plain next(), so every slide stays
//     reachable by tapping alone — a single-pointer path through all of them — even when the slider
//     isn't set to infinite loop. A forward-only stop-at-last would strand the user needing a swipe
//     (a drag) to reach an earlier slide, which would re-break WCAG 2.5.7.

(() => {
  const MOBILE_MAX = 749;
  const DRAG_THRESHOLD = 10; // px of movement that makes it a swipe, not a tap
  const INTERACTIVE =
    'a, button, [role="button"], input, select, textarea, label, summary, dialog, dialog-component, .hero-slide__drawer';

  const bind = (slideshow) => {
    if (slideshow.dataset.tapAdvanceBound) return;
    slideshow.dataset.tapAdvanceBound = '1';

    let startX = 0;
    let startY = 0;
    let moved = false;

    slideshow.addEventListener(
      'pointerdown',
      (event) => {
        startX = event.clientX;
        startY = event.clientY;
        moved = false;
      },
      { passive: true }
    );

    slideshow.addEventListener(
      'pointermove',
      (event) => {
        if (
          Math.abs(event.clientX - startX) > DRAG_THRESHOLD ||
          Math.abs(event.clientY - startY) > DRAG_THRESHOLD
        ) {
          moved = true;
        }
      },
      { passive: true }
    );

    slideshow.addEventListener('click', (event) => {
      if (window.innerWidth > MOBILE_MAX) return; // desktop has keyboard + (optionally) controls
      if (moved) return; // it was a swipe/drag, not a tap
      if (event.target.closest(INTERACTIVE)) return; // let CTAs / the drawer work

      // Only the active slide advances; peeking slides keep their own navigate-to-slide click.
      const slide = event.target.closest('slideshow-slide');
      if (!slide || slide.getAttribute('aria-hidden') !== 'false') return;

      // Cycle forward, wrapping after the last slide so every slide stays tap-reachable.
      const slides = [...slideshow.querySelectorAll('slideshow-slide')];
      if (slides.indexOf(slide) === slides.length - 1) {
        slideshow.select?.(0);
      } else {
        slideshow.next?.();
      }
    });
  };

  const init = (root) => {
    (root || document)
      .querySelectorAll('slideshow-component.hero-slider--tap-advance-mobile')
      .forEach(bind);
  };

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', () => init());
  }

  // Theme editor: a section re-render swaps in a fresh slideshow (the bound flag is lost) — re-bind.
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (root && typeof root.querySelectorAll === 'function') init(root);
  });
})();
